import { useCallback } from 'react';
import { useRouter } from 'expo-router';

import { type PartialProduct } from '../../graphql';
import { useDeliveryOrderInFlight } from '../useDeliveryOrderInFlight';

// ────────────────────────────────────────────────────────────────────────────────

export const useNavigateToCustomization = (props: Params) => {
  const { restaurantSlug, deliveryAddressId } = props;

  const router = useRouter();
  const { inFlightDeliveryOrderId, navigateToDeliveryOrderInFlightScreen } =
    useDeliveryOrderInFlight({ requestPolicy: 'cache-only' });

  // ─── HELPERS ─────────────────────────────────────────────────────

  const navigateToCustomization = useCallback(
    (productSlug: string) => {
      if (!restaurantSlug) return;

      router.navigate({
        pathname: '/[restaurantSlug]/[productSlug]',
        params: { restaurantSlug, productSlug },
      });
    },
    [restaurantSlug, router],
  );

  const navigateToCustomizationDelivery = useCallback(
    (productSlug: string) => {
      if (!deliveryAddressId) return;

      // Block delivery pdp navigation when there is an in-flight delivery order.
      if (inFlightDeliveryOrderId) {
        navigateToDeliveryOrderInFlightScreen();

        return;
      }

      router.navigate({
        pathname: '/delivery/[addressId]/[productSlug]',
        params: { addressId: deliveryAddressId, productSlug },
      });
    },
    [deliveryAddressId, inFlightDeliveryOrderId, navigateToDeliveryOrderInFlightScreen, router],
  );

  // ─────────────────────────────────────────────────────────────────

  return useCallback(
    (product: PartialProduct) => {
      const { slug: productSlug = '' } = product;

      if (deliveryAddressId) {
        navigateToCustomizationDelivery(productSlug);
        return;
      }

      navigateToCustomization(productSlug);
    },
    [deliveryAddressId, navigateToCustomizationDelivery, navigateToCustomization],
  );
};

// ─── TYPES ──────────────────────────────────────────────────────────────────────

type Params = {
  restaurantSlug?: string;
  deliveryAddressId?: string;
};
